import type { EnhancerEntrySurface } from '@/analytics/enhancer-types';
import {
  type AdminV2PaywallPopupBranchSlug,
  type AdminV2PaywallUpgradeReason,
  adminV2PaywallPopupBranchSlugs,
} from '@/site-runtime/code-owned/paywall-branches';

export const checkoutAnalyticsSurfaces = [
  'pricing_modal',
  'pricing_side_panel',
  'standalone_page',
  'upgrade_dialog',
] as const;

export type CheckoutAnalyticsSurface =
  (typeof checkoutAnalyticsSurfaces)[number];

export type CheckoutAnalyticsAttribution = {
  firstTouchSource?: string;
  firstTouchMedium?: string;
  firstTouchCampaign?: string;
  firstTouchContent?: string;
  firstTouchTerm?: string;
  firstTouchLandingPage?: string;
  firstTouchClickId?: string;
  lastTouchSource?: string;
  lastTouchMedium?: string;
  lastTouchCampaign?: string;
  lastTouchContent?: string;
  lastTouchTerm?: string;
  lastTouchLandingPage?: string;
  lastTouchClickId?: string;
};

export type CheckoutAnalyticsInput = {
  conversionId: string;
  checkoutSurface: CheckoutAnalyticsSurface;
  paywallBranchSlug?: AdminV2PaywallPopupBranchSlug;
  enhancerSessionId?: string;
  enhancerToolId?: string;
  enhancerEntrySurface?: EnhancerEntrySurface;
  sourceUiSurface?: string;
  attribution?: CheckoutAnalyticsAttribution;
};

export type StoredCheckoutAnalyticsContext = CheckoutAnalyticsInput & {
  analyticsClientId: string | null;
  paywallReason: AdminV2PaywallUpgradeReason | null;
};

export function createCheckoutConversionId() {
  return `cv_${crypto.randomUUID()}`;
}

const paywallReasonByBranchSlug = {
  featureBlocked: 'paid-feature',
  paidTool: 'paid-tool',
  paidSetting: 'paid-setting',
  batchAccess: 'batch-access',
  uploadSizeLimit: 'upload-size-limit',
  creditsInsufficient: 'insufficient-credits',
  concurrencyLimit: 'concurrency-limit',
  upgradeRequired: 'upgrade-intent',
} as const satisfies Record<
  AdminV2PaywallPopupBranchSlug,
  AdminV2PaywallUpgradeReason
>;

export function resolvePaywallReasonFromBranchSlug(
  branchSlug: AdminV2PaywallPopupBranchSlug | null | undefined
): AdminV2PaywallUpgradeReason | null {
  return branchSlug ? paywallReasonByBranchSlug[branchSlug] : null;
}

export function isCheckoutAnalyticsSurface(
  value: unknown
): value is CheckoutAnalyticsSurface {
  return checkoutAnalyticsSurfaces.includes(value as CheckoutAnalyticsSurface);
}

export function isPaywallBranchSlug(
  value: unknown
): value is AdminV2PaywallPopupBranchSlug {
  return adminV2PaywallPopupBranchSlugs.includes(
    value as AdminV2PaywallPopupBranchSlug
  );
}

function readOptionalStoredString(value: unknown) {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : undefined;
}

function readStoredAttribution(
  value: unknown
): CheckoutAnalyticsAttribution | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const record = value as Record<string, unknown>;
  return {
    firstTouchSource: readOptionalStoredString(record.firstTouchSource),
    firstTouchMedium: readOptionalStoredString(record.firstTouchMedium),
    firstTouchCampaign: readOptionalStoredString(record.firstTouchCampaign),
    firstTouchContent: readOptionalStoredString(record.firstTouchContent),
    firstTouchTerm: readOptionalStoredString(record.firstTouchTerm),
    firstTouchLandingPage: readOptionalStoredString(
      record.firstTouchLandingPage
    ),
    firstTouchClickId: readOptionalStoredString(record.firstTouchClickId),
    lastTouchSource: readOptionalStoredString(record.lastTouchSource),
    lastTouchMedium: readOptionalStoredString(record.lastTouchMedium),
    lastTouchCampaign: readOptionalStoredString(record.lastTouchCampaign),
    lastTouchContent: readOptionalStoredString(record.lastTouchContent),
    lastTouchTerm: readOptionalStoredString(record.lastTouchTerm),
    lastTouchLandingPage: readOptionalStoredString(record.lastTouchLandingPage),
    lastTouchClickId: readOptionalStoredString(record.lastTouchClickId),
  };
}

export function parseStoredCheckoutAnalyticsContext(
  value: unknown
): StoredCheckoutAnalyticsContext | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const conversionId = readOptionalStoredString(record.conversionId);
  if (
    !conversionId ||
    !/^cv_[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      conversionId
    ) ||
    !isCheckoutAnalyticsSurface(record.checkoutSurface)
  ) {
    return null;
  }
  const paywallBranchSlug = isPaywallBranchSlug(record.paywallBranchSlug)
    ? record.paywallBranchSlug
    : undefined;
  const enhancerEntrySurface =
    record.enhancerEntrySurface === 'home' ||
    record.enhancerEntrySurface === 'tool_page' ||
    record.enhancerEntrySurface === 'pricing' ||
    record.enhancerEntrySurface === 'settings' ||
    record.enhancerEntrySurface === 'other'
      ? record.enhancerEntrySurface
      : undefined;
  const analyticsClientId =
    typeof record.analyticsClientId === 'string' &&
    /^\d+\.\d+$/.test(record.analyticsClientId)
      ? record.analyticsClientId
      : null;

  return {
    conversionId,
    checkoutSurface: record.checkoutSurface,
    analyticsClientId,
    paywallReason: resolvePaywallReasonFromBranchSlug(paywallBranchSlug),
    ...(paywallBranchSlug ? { paywallBranchSlug } : {}),
    ...(readOptionalStoredString(record.enhancerSessionId)
      ? {
          enhancerSessionId: readOptionalStoredString(record.enhancerSessionId),
        }
      : {}),
    ...(readOptionalStoredString(record.enhancerToolId)
      ? { enhancerToolId: readOptionalStoredString(record.enhancerToolId) }
      : {}),
    ...(enhancerEntrySurface ? { enhancerEntrySurface } : {}),
    ...(readOptionalStoredString(record.sourceUiSurface)
      ? { sourceUiSurface: readOptionalStoredString(record.sourceUiSurface) }
      : {}),
    ...(record.attribution
      ? { attribution: readStoredAttribution(record.attribution) }
      : {}),
  };
}
